import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { first, firstValueFrom, Observable } from 'rxjs';
import { AuthApiService } from 'src/app/apis/auth-api.service';
import { LocalStorageService } from 'src/app/lib/local-storage.service';
import { ErrorResponse } from 'src/app/model/error';
import * as EmployeeActions from './../../store/actions/employee.actions';
import * as AppActions from './../../store/actions/app.actions';
declare let $: any;

import { Preferences } from '@capacitor/preferences';
import { UtilsService } from 'src/app/lib/utils.service';
import { TranslateService } from '@ngx-translate/core';
import { Common } from 'src/app/lib/common-methods';
import { NgxSpinnerService } from 'ngx-spinner';
import { CapacitorStorageService } from 'src/app/lib/capacitor-storage.service';
import moment from 'moment';
import { Capacitor } from '@capacitor/core';

const CREDS_SEPERATOR = "_SAKIS_";

//TODO: we can try this fo IOS
//https://www.npmjs.com/package/capacitor-ios-autofill-save-password
//https://stackoverflow.com/questions/58530929/how-do-i-turn-on-the-webview-autocomplete-for-an-login-form-in-ionics-capacitor/67970502#67970502

type LangOption = { img: string, text: string, shortText: string, value: string };

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  languages: LangOption[] = [
    { img: 'gr.svg', text: 'Greek', value: 'el', shortText: 'GR' },
    { img: 'en.svg', text: 'English', value: 'en', shortText: 'EN' },
    { img: 'hr.svg', text: 'Croatian', value: 'hr', shortText: 'HR' },
    { img: 'rs.svg', text: 'Serbian', value: 'sr', shortText: 'SR' },
    { img: 'de.svg', text: 'German', value: 'de', shortText: 'DE' },
    { img: 'ru.svg', text: 'Russian', value: 'ru', shortText: 'RU' },
  ];

  language: LangOption;
  showPassword: boolean = false;
  username: string;
  password: string;
  error: string | null = null;

  constructor(
    private authApiService: AuthApiService,
    private router: Router,
    private route: ActivatedRoute,
    private localStorage: LocalStorageService,
    private capacitorStorageService: CapacitorStorageService,
    private utilsService: UtilsService,
    private translateService: TranslateService,
    private store: Store<{}>,
    private spinner: NgxSpinnerService) {
    this.username = '';
    this.password = '';
    this.language = this.languages.find(l => l.value == localStorage.getLanguage()) ?? this.languages[0];
  }

  async ngOnInit() {    
    $('body').removeClass('top-header-menu-close');

    // Auto Login
    if (Capacitor.getPlatform() == 'web' && this.router.isActive('/auth/auto', false)) {
      this.autoLogin();
      return;
    }

    await this.loadData();

    const explicitLoggedOut = await this.capacitorStorageService.getExplicitLogout();

    if (explicitLoggedOut === false) {
      // try to auto login
      
      // ensure one try only to auto login
      this.capacitorStorageService.setExplicitLogout(true);

      // check if the credentials exist
      const { value } = await Preferences.get({ key: 'creds' });

      if (value != null) {
        this.login();
      }
    }
    else {
      this.capacitorStorageService.setAds(null);
      this.capacitorStorageService.setAdsNextDisplayDayTime(null);
      this.capacitorStorageService.setAdsTimesDisplayed(null);
    }
  }

  private loadData = async () => {
    const { value } = await Preferences.get({ key: 'creds' });
    if (value == null) {
      return;
    }

    let invalid = false;

    if (value != null && value.indexOf(CREDS_SEPERATOR) > -1) {
      const creds = value.split(CREDS_SEPERATOR);
      if (creds.length == 2) {
        this.username = creds[0];
        this.password = creds[1];
      } else {
        invalid = true;
      }
    } else {
      invalid = true;
    }

    if (invalid) {
      await Preferences.remove({ key: 'creds' });
    }
  }

  setLanguage(lang: LangOption) {
    this.language = lang;
    this.localStorage.setLanguage(this.language.value);
  }

  async login() {
    const userName = this.username.trim();
    const password = this.password.trim();

    if (Common.isNullOrEmpty(userName) ||
      Common.isNullOrEmpty(password)) {
      this.error = this.translateService.instant('login.requiredFields');
      return;
    }

    this.localStorage.setLanguage(this.language.value);
    this.spinner.show();
    this.authApiService.logIn(userName, password, new Date())
      .pipe(first())
      .subscribe(async (a) => {
        this.spinner.hide();
        this.error = null;
        // load employee data
        this.store.dispatch({ type: EmployeeActions.loadEmployee.type });

        if (!this.utilsService.canWrite('LogIn')) {
          this.localStorage.clear();
          this.router.navigate(["/auth/login"]);
          this.error = this.translateService.instant('login.noLoginPersmission');
          return;
        }

        if (this.localStorage.isAuthenticated()) {
          await Preferences.set({
            key: 'creds',
            value: userName + CREDS_SEPERATOR + password
          });

          const latestActiveView = await this.capacitorStorageService.getLatestActiveView();

          if (latestActiveView === 'TablesPlan') {
            this.router.navigate(['/tables-plan']);
          } else {
            this.router.navigate(['/']);
          }
        }

      }, (err: ErrorResponse) => {
        this.spinner.hide();
        if (err.ErrorCode == "1200000005") {
          this.error = this.translateService.instant('login.wrongCredentials');
        } else {
          this.error = err.ErrorDescription;
        }
      });
  }

  help() {
    this.store.dispatch({ type: AppActions.help.type });
  }

  autoLogin() {
    const user = this.route.snapshot.paramMap.get('user');
    const pass = this.route.snapshot.paramMap.get('pass');

    if (user == null || user.trim() == "" || pass == null || user.trim() == "") return;

    this.username = user;
    this.password = pass;

    this.login();
  }

}
